import * as Blockly from 'blockly/core';
import { blockSound } from '@/(routers)/boards/editor/block/blockSound';

export interface SegmentedToggleOptions extends Blockly.FieldConfig {
    options: { value: string; label: string }[];
    onChange?: (value: string) => void;
    activeColor?: string;
}

export class FieldSegmentedToggle extends Blockly.Field<string> {
    private options_: { value: string; label: string }[];
    private onChange_?: (value: string) => void;
    private activeColor_: string;
    private foreignObject_: SVGForeignObjectElement | null = null;

    constructor(value: string, options: SegmentedToggleOptions) {
        super(value);
        this.options_ = options.options;
        this.onChange_ = options.onChange;
        this.activeColor_ = options.activeColor ?? '#FFFFFF';
        this.SERIALIZABLE = false;
    }

    override toXml(fieldElement: Element): Element {
        return fieldElement;
    }

    override fromXml(_fieldElement: Element): void {
        // 何もしない。型管理はdomToMutationで行う
    }

    override initView() {
        const fo = Blockly.utils.dom.createSvgElement(
            'foreignObject' as any,
            { width: this.getWidth_(), height: 34 },
            this.fieldGroup_,
        ) as unknown as SVGForeignObjectElement;
        fo.style.pointerEvents = 'all';
        fo.style.overflow = 'visible';
        this.foreignObject_ = fo;
        this.renderContent_();
    }

    private getWidth_(): number {
        const maxLen = Math.max(...this.options_.map((o) => o.label.length));
        return this.options_.length * (maxLen * 12 + 20);
    }

    private renderContent_(currentValue?: string) {
        if (!this.foreignObject_) return;
        const fo = this.foreignObject_;
        const activeVal = currentValue ?? (this as any).value_ ?? this.getValue();

        while (fo.firstChild) fo.removeChild(fo.firstChild);

        fo.setAttribute('width', String(this.getWidth_()));
        fo.setAttribute('height', '34');

        const ns = 'http://www.w3.org/1999/xhtml';
        const div = document.createElementNS(ns, 'div') as HTMLDivElement;
        div.style.cssText = `display:flex;border-radius:6px;overflow:hidden;border:1.5px solid rgba(255,255,255,0.6);height:34px;box-sizing:border-box;width:${this.getWidth_()}px;`;

        for (const opt of this.options_) {
            const seg = document.createElementNS(ns, 'div') as HTMLDivElement;
            const isActive = opt.value === activeVal;
            seg.textContent = opt.label;
            seg.style.cssText = `flex:1;padding:0 1px;font-size:16px;font-weight:500;display:flex;align-items:center;justify-content:center;cursor:${
                isActive ? 'default' : 'pointer'
            };transition:all 0.15s;background:${
                isActive ? 'rgba(255,255,255,0.3)' : 'transparent'
            };color:white;user-select:none;`;
            seg.onclick = (e) => {
                e.stopPropagation();
                e.preventDefault();
                const currentVal = (this as any).value_ ?? this.getValue();
                if (opt.value === currentVal) return;
                blockSound.queue('dropdown');
                (this as any).value_ = opt.value;
                this.onChange_?.(opt.value);
                this.renderContent_(opt.value);
            };
            div.appendChild(seg);
        }

        fo.appendChild(div);
    }

    override render_() {
        this.renderContent_();
    }

    override doValueUpdate_(newValue: string) {
        super.doValueUpdate_(newValue);
        if (!this.foreignObject_) return;
        this.renderContent_();
    }

    override showEditor_(e?: Event) {
        if (!this.foreignObject_) return;
        const mouseEvent = e as MouseEvent;
        if (!mouseEvent?.clientX) return;
        const rect = this.foreignObject_.getBoundingClientRect();
        const relX = mouseEvent.clientX - rect.left;
        const segWidth = rect.width / this.options_.length;
        const index = Math.floor(relX / segWidth);
        const opt = this.options_[index];
        if (!opt) return;
        const currentVal = (this as any).value_ ?? this.getValue();
        if (opt.value === currentVal) return;
        blockSound.queue('dropdown');
        (this as any).value_ = opt.value;
        this.onChange_?.(opt.value);
        this.renderContent_(opt.value);
    }

    setActiveColor(color: string) {
        this.activeColor_ = color;
        this.renderContent_();
    }

    override getSize() {
        return new Blockly.utils.Size(this.getWidth_(), 34);
    }

    override getDisplayText_() {
        return this.options_.find((o) => o.value === this.getValue())?.label ?? '';
    }
}
